import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { Editor, generateHTML } from '@tiptap/core';
import {
  CustomGroupSectionNode,
  CustomSectionNode,
} from 'api/custom-section/types';
import {
  JSONContent,
  PageType,
  SourceType,
  TiptapJson,
} from 'api/page-template/types';
import {
  CreatePeoplePageTemplatePayload,
  PeoplePageTemplate,
  UpdatePeoplePageTemplatePayload,
} from 'api/people-page-template/types';
import { isEqual } from 'lodash';
import { getTipTapJsonFromState } from 'components/organisms/company-page-template/utils/json-from-source';
import { isCustomGroupSection } from 'components/organisms/document-editor/extensions/custom-group-section/utils';
import { isCustomSectionNode } from 'components/organisms/document-editor/extensions/custom-section/utils';
import { PeoplePageTemplateState } from '../manage/types';

const getCustomSections = (
  nodes: JSONContent[],
): (CustomSectionNode | CustomGroupSectionNode)[] => {
  let result: (CustomSectionNode | CustomGroupSectionNode)[] = [];

  for (const node of nodes) {
    if (isCustomSectionNode(node) || isCustomGroupSection(node)) {
      result.push(node);
    }
    if (isCustomGroupSection(node) && Array.isArray(node.content)) {
      result = result.concat(getCustomSections(node.content));
    }
  }

  return result;
};
const commonKeys = [
  'sectionType',
  'contentState',
  'templateSectionId',
  'documentSectionId',
  'title',
  'config',
];
const isSameCustomNode = (
  source: CustomSectionNode | CustomGroupSectionNode,
  other: CustomSectionNode | CustomGroupSectionNode,
) => {
  if (source.type !== other.type) {
    return false;
  }
  if (isCustomGroupSection(source)) {
    return (
      isEqual(source.attrs, other.attrs) &&
      source.content.length === other.content.length
    );
  }

  return commonKeys.every((key) =>
    isEqual(source.attrs[key], other.attrs[key]),
  );
};
const getDifferences = (tiptapJson: TiptapJson, previousJson: TiptapJson) => {
  const previousCustomSections = getCustomSections(previousJson.content);
  const latestCustomSections = getCustomSections(tiptapJson.content);

  if (previousCustomSections.length === 0) {
    return {
      sectionsAdded: latestCustomSections,
      sectionsRemoved: [],
      sectionsUpdated: [],
    };
  }

  const previousSectionsMap = new Map(
    previousCustomSections.map((section) => [
      section.attrs.templateSectionId,
      section,
    ]),
  );
  const latestSectionsMap = new Map(
    latestCustomSections.map((section) => [
      section.attrs.templateSectionId,
      section,
    ]),
  );
  const sectionsAdded = latestCustomSections.filter(
    (section) => !previousSectionsMap.has(section.attrs.templateSectionId),
  );
  const sectionsRemoved = previousCustomSections.filter(
    (section) => !latestSectionsMap.has(section.attrs.templateSectionId),
  );
  const sectionsUpdated = latestCustomSections.filter((section) => {
    const templateSectionId = section.attrs.templateSectionId;

    if (!previousSectionsMap.has(templateSectionId)) {
      return false;
    }

    const previousSection = previousSectionsMap.get(templateSectionId);

    if (previousSection == null) {
      return false;
    }

    return !isSameCustomNode(section, previousSection);
  });

  return {
    sectionsAdded,
    sectionsRemoved,
    sectionsUpdated,
  };
};
const getOperations = (tiptapJson: TiptapJson, previousJson: TiptapJson) => {
  const { sectionsAdded, sectionsRemoved, sectionsUpdated } = getDifferences(
    tiptapJson,
    previousJson,
  );

  return [
    ...sectionsAdded.map((section) => ({
      operation: 'POST' as const,
      payload: section,
    })),
    ...sectionsRemoved.map((section) => ({
      operation: 'DELETE' as const,
      payload: section,
    })),
    ...sectionsUpdated.map((section) => ({
      operation: 'PUT' as const,
      payload: section,
    })),
  ];
};

export const stateToUpdateApiPayload = (
  state: PeoplePageTemplateState,
  editor: Editor,
  previous: PeoplePageTemplate,
  fieldsLookup: GroupFieldsMeta,
): UpdatePeoplePageTemplatePayload => {
  const filterTransformer = getApiGroupedFilterTransformer(
    fieldsLookup,
    FilterGroupType.User,
  );
  const tiptapJson = getTipTapJsonFromState(state.tiptapJson);
  const html = generateHTML(tiptapJson, editor.extensionManager.extensions);

  return {
    ...previous,
    templateName: state.name,
    operations: getOperations(tiptapJson, previous.tiptapJson),
    tiptapJson,
    htmlPayload: html,
    filters: {
      pageTriggerCondition: filterTransformer.to(state.filter),
    },
  };
};

export const stateToCreateApiPayload = (
  state: PeoplePageTemplateState,
  editor: Editor,
  fieldsLookup: GroupFieldsMeta,
): CreatePeoplePageTemplatePayload => {
  const filterTransformer = getApiGroupedFilterTransformer(
    fieldsLookup,
    FilterGroupType.User,
  );
  const tiptapJson = getTipTapJsonFromState(state.tiptapJson);
  const html = generateHTML(tiptapJson, editor.extensionManager.extensions);

  return {
    sourceType: SourceType.CustomerUser,
    pageType: PageType.People,
    templateName: state.name,
    operations: getOperations(tiptapJson, {
      content: [],
      type: 'doc',
    }),
    tiptapJson,
    htmlPayload: html,
    filters: {
      pageTriggerCondition: filterTransformer.to(state.filter),
    },
  };
};
